'use client';

import Flex from '@commons/containers/Flex';
import { useMediaQueries } from '@commons/contexts/responsive';
import PageBuilder from '@commons/pagebuilder';
import type { DrupalBlock, DrupalParagraph } from '@commons/types/drupal';
import type { Spacing } from '@commons/types/styles';

import Carousel from './Carousel';

/**
 * Lazy loading the carousel to reduce the initial bundle size.
 *
 * This does not work when used in a pagebuilder factory directly, because every items are loaded at once.
 * Doing it in the component itself ensures that the chunks are only loaded when the component is rendered.
 *
 * @see https://nextjs.org/docs/app/building-your-application/optimizing/lazy-loading
 */

/**
 * Type definition for the CarouselBlock component properties.
 */

export type CarouselBlockProps = DrupalBlock<{
  slides: DrupalParagraph[];
  slidesToShow: string | number;
  autoplay: boolean;
  displayDots: boolean;
  displayArrows: boolean;
  displayCounter: boolean;
  enableOnMobile: boolean;
  bleedEffect: boolean;
  gap: Spacing;
}>;

/**
 * CarouselBlockFactory component.
 *
 * Checks the screen size and if its enabled on mobile to render the appropriate component.
 */

const CarouselBlockFactory = (props: CarouselBlockProps) => {
  const { ready, minWidth } = useMediaQueries();

  // Only load the carousel when we know the screen size.
  const isMobile = !ready || !minWidth.md;

  const { slides, enableOnMobile = true } = props.content;

  if (isMobile && !enableOnMobile) {
    return (
      <Flex gap="md" direction="column">
        {slides.map(PageBuilder.renderParagraph)}
      </Flex>
    );
  }

  return <Carousel {...props} />;
};

export default CarouselBlockFactory;
